import moment from "moment";
import { buildFormFieldsHtml, escapeHtml } from "./formStructuredPrint.js";
import { resolveFieldTranslation } from "./formTranslations.js";
import { resolveNodeTranslation } from "./workflowTranslations.js";
import { getVisibleWidgets } from "./requestHelper.js";
import { getLocalDateFormatForWorkflow, getLocalFormattedDate, getLocalFormattedDateWithoutTime } from "./getLocalFormattedDate.js";

// Prints a request as a clean A4 document instead of photographing the screen: the
// forms (with their submitted values) run full width, and the workflow follows
// underneath as a horizontal chain of steps joined by arrows. The body is built
// from the form definitions the same way the public-form print is, so nothing
// arrives clipped, cut off mid-input or blank the way a screenshot of the live
// antd form does.

const STATUS_COLORS = {
    approved: { color: "#50A248", bg: "#EEF6ED" },
    rejected: { color: "#F5222D", bg: "#FFF1F0" },
    pending: { color: "#FAAD14", bg: "#FFFBE6" },
    submitted: { color: "#141BCB", bg: "#E8E8FA" },
    skipped: { color: "#8C8C8C", bg: "#F5F5F5" },
    closed: { color: "#E84545", bg: "#FDF2F2" },
};

const titleCase = (value) => {
    const text = String(value || "").trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const DEFAULT_THEME = { color: "#141BCB", bg: "#E8E8FA" };

// Statuses are matched case-insensitively: a step storing "Rejected" has to land
// on the same red as one storing "rejected".
const themeFor = (map, status) => map[String(status || "").trim().toLowerCase()] || DEFAULT_THEME;

const statusPillHtml = (status) => {
    if (!status) return "";
    const theme = themeFor(STATUS_COLORS, status);
    return `<span class="wf-status" style="color:${theme.color};background:${theme.bg};">${escapeHtml(titleCase(status))}</span>`;
};

const metaRowHtml = (label, value) =>
    value ? `<div class="wf-row"><span class="wf-key">${escapeHtml(label)}</span><span class="wf-value">${escapeHtml(value)}</span></div>` : "";


// Approver tag colours, matching the antd tags the workflow card uses on screen.
const TAG_COLORS = {
    approved: { color: "#389E0D", bg: "#F6FFED" },
    rejected: { color: "#CF1322", bg: "#FFF1F0" },
};

// One approval history entry: the approver as a status-coloured tag, the date it
// happened under it, then any comment they left.
const stepStatusHtml = (status) => {
    const tag = themeFor(TAG_COLORS, status.approvalStatus);
    // The tag colour already carries the outcome, so the date stands on its own.
    return `
    <div class="wf-history">
        <span class="wf-tag" style="color:${tag.color};background:${tag.bg};">${escapeHtml(status.actionBy || "")}</span>
        ${status.updatedOn ? `<div class="wf-history-date">${escapeHtml(getLocalFormattedDate(status.updatedOn))}</div>` : ""}
        ${status.comment ? `<div class="wf-comment">${escapeHtml(status.comment)}</div>` : ""}
    </div>`;
};

const workflowStepHtml = (step, index, { lng, submitterName }) => {
    const translated = resolveNodeTranslation(step, lng) || step;
    const name = translated.name || step.name || `Step ${index + 1}`;
    const status = step.approvalStatus || (String(step.name).toLowerCase() === "approved" ? "approved" : "");
    const statuses = Array.isArray(step.statuses) ? step.statuses : [];
    // A step that carries an approval history already records who acted, when and
    // with what comment — the step-level fields hold the same values, so printing
    // both repeats every line. The history wins where it exists.
    // The requester step reads like the approval entries — who submitted it over a
    // muted date — rather than as labelled rows; the header already says Created
    // By / Created On. Approval steps keep their labels so it is clear who acted
    // on them.
    const actionHtml = statuses.length > 0
        ? ""
        : `${index === 0
            ? `<div class="wf-history">
                <span class="wf-submitter">${escapeHtml(step.actionBy || submitterName || "")}</span>
                ${step.actionOn ? `<div class="wf-history-date">${escapeHtml(getLocalDateFormatForWorkflow(step.actionOn))}</div>` : ""}
            </div>`
            : `${metaRowHtml("Action by", step.actionBy || "")}
            ${step.actionOn ? metaRowHtml("Action on", getLocalDateFormatForWorkflow(step.actionOn)) : ""}`}
            ${step.comment ? `<div class="wf-comment">${escapeHtml(step.comment)}</div>` : ""}`;

    return `
        <div class="wf-step">
            <div class="wf-head">
                <span class="wf-name">${escapeHtml(name)}</span>
                ${statusPillHtml(status)}
            </div>
            ${actionHtml}
            ${step.dueDate ? metaRowHtml("Due date", getLocalFormattedDateWithoutTime(step.dueDate)) : ""}
            ${step.delayed ? metaRowHtml("Delayed", "Yes") : ""}
            ${statuses.length > 0
            ? `<div class="wf-statuses">${statuses.map(stepStatusHtml).join("")}</div>`
            : ""}
        </div>`;
};

// One form: its header (unless the builder hid it) plus the label / value body.
const formHtml = (form, { values, conditions, currentStatus, lng, userInfo, userGroups, masterList }) => {
    const elements = form?.items || [];
    if (elements.length === 0) return "";

    const formConditions = (conditions || []).filter((condition) => condition.formId === form?.formId);
    const visibleWidgets = getVisibleWidgets(elements, { ...values }, userInfo, userGroups, currentStatus, formConditions);
    const visibleIds = new Set(visibleWidgets.map((widget) => widget.id));
    const body = buildFormFieldsHtml({ formElements: elements, values, lng, visibleIds, masterList });
    if (!body.trim()) return "";

    const header = resolveFieldTranslation(form?.formHeader || {}, lng) || {};
    const heading = header.hideHeader ? "" : `<div class="form-title">${escapeHtml(header.label || "Intake Form")}</div>`;
    return `<div class="form-block">${heading}${body}</div>`;
};

const headerHtml = (meta = {}) => {
    // The case id leads the title, with the space name after it.
    const title = [meta.caseId, meta.title || "Request"].filter(Boolean).join(": ");
    const bits = [
        meta.status ? `Status: ${titleCase(meta.status)}` : "",
        meta.createdBy ? `Created By: ${meta.createdBy}` : "",
        meta.createdOn ? `Created On: ${getLocalDateFormatForWorkflow(meta.createdOn)}` : "",
        meta.cycle ? `Current Cycle: ${meta.cycle}` : "",
    ].filter(Boolean);

    return `
        <div class="doc-header">
            <div class="doc-title">${escapeHtml(title)}</div>
            ${bits.length ? `<div class="doc-subtitle">${bits.map(escapeHtml).join("&nbsp;&nbsp;&nbsp;&nbsp;")}</div>` : ""}
            <div class="doc-printed">Printed on ${escapeHtml(moment().format("DD MMM, YYYY hh:mm a"))}</div>
        </div>`;
};

// Browsers name the saved PDF after the printed document's title, so this is what
// the Save-as-PDF dialog offers as the file name: "<caseId> - <space name>.pdf".
const documentName = (meta = {}) =>
    [meta.caseId, meta.title].filter(Boolean).join(" - ") || "Request";

const DOCUMENT_STYLES = `
    /* Only the margins are fixed. Naming a size here ("A4 portrait") is what made
       the browser's own Layout and Paper size controls disappear from its print
       dialog: a document that states its page size leaves the reader nothing to
       choose. Left out, the dialog offers both again and the document lays itself
       out to whatever is picked - the body is sized in percentages and the
       workflow chain wraps to the width it is given. */
    @page { margin: 12mm 10mm; }
    * { box-sizing: border-box; }
    html, body { overflow-x: hidden; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111827; background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    /* Table widgets ship with a horizontal scroller for the screen; on paper the
       tables are already width-constrained, so the scrollbar only adds a grey
       band across the page. */
    .print-table-scroll { overflow: visible !important; }
    .doc-header { padding-bottom: 12px; margin-bottom: 14px; border-bottom: 2px solid #141bcb; }
    .doc-title { font-size: 20px; font-weight: 700; line-height: 1.3; }
    .doc-subtitle { margin-top: 6px; font-size: 12px; color: #6b7280; }
    .doc-printed { margin-top: 4px; font-size: 11px; color: #9ca3af; }
    .forms-section { width: 100%; }
    .workflow-section { margin-top: 20px; padding-top: 14px; border-top: 2px solid #e5e7eb; }
    .col-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; margin-bottom: 10px; }
    /* The workflow reads left to right and wraps onto the next row when it runs
       out of page width; the arrows are chain links between the cards. Three
       cards plus their arrows come to 702px, just inside the 718px an A4 page
       gives at 10mm margins. */
    .wf-chain { display: flex; flex-wrap: wrap; align-items: stretch; }
    .wf-arrow { flex: 0 0 28px; display: flex; align-items: center; justify-content: center; color: #bfbfbf; font-size: 18px; margin-bottom: 22px; }
    .form-block { margin-bottom: 20px; }
    .form-title { font-size: 20px; font-weight: 700; color: #141bcb; padding-bottom: 7px; margin-bottom: 6px; border-bottom: 2px solid #e5e7eb; break-after: avoid; page-break-after: avoid; }
    .wf-step { flex: 0 0 206px; width: 206px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; margin-bottom: 22px; break-inside: avoid; page-break-inside: avoid; }
    .wf-head { margin-bottom: 6px; }
    .wf-name { display: block; font-size: 13px; font-weight: 700; line-height: 1.4; word-break: break-word; }
    .wf-status { display: inline-block; margin-top: 4px; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px; white-space: nowrap; }
    .wf-row { font-size: 11px; line-height: 1.5; margin-top: 3px; }
    .wf-key { display: block; color: #6b7280; }
    .wf-value { color: #111827; word-break: break-word; }
    .wf-comment { margin-top: 6px; padding: 6px 8px; background: #f5f5f5; border-radius: 6px; font-size: 11px; color: #374151; word-break: break-word; }
    .wf-statuses { margin-top: 8px; }
    .wf-history { margin-bottom: 6px; }
    /* Only between entries: a step with several approvals gets a dotted rule
       separating them, while a single approval keeps no rule at all. */
    .wf-history + .wf-history { padding-top: 6px; border-top: 1px dotted #d0d5dd; }
    .wf-tag { display: inline-block; font-size: 11px; padding: 1px 7px; border-radius: 4px; word-break: break-all; }
    .wf-submitter { display: inline-block; font-size: 11px; color: #000000A6; word-break: break-all; }
    .wf-history-date { font-size: 10px; color: #00000073; margin-top: 3px; }
    .empty { font-size: 12px; color: #c7ccd1; padding: 8px 0; }
`;

// Builds the complete printable document. Exported separately from the print call
// so the markup can be inspected without opening a print dialog.
export const buildRequestPrintHtml = ({
    forms = [],
    values = {},
    workflow = [],
    conditions = [],
    currentStatus = "",
    lng = "",
    userInfo = {},
    userGroups = [],
    masterList = [],
    meta = {},
    submitterName = "",
}) => {
    const formsHtml = forms
        .map((form) => formHtml(form, { values, conditions, currentStatus, lng, userInfo, userGroups, masterList }))
        .join("");
    // Arrows sit between the step cards, turning the list into a left-to-right
    // chain that wraps onto further rows when the workflow is a long one.
    const stepsHtml = workflow
        .map((step, index) => workflowStepHtml(step, index, { lng, submitterName }))
        .join(`<div class="wf-arrow">&#10132;</div>`);

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(documentName(meta))}</title>
<style>${DOCUMENT_STYLES}</style>
</head>
<body>
    ${headerHtml(meta)}
    <div class="forms-section">
        ${formsHtml || `<div class="empty">No form data was captured.</div>`}
    </div>
    <div class="workflow-section">
        <div class="col-title">Workflow</div>
        <div class="wf-chain">
            ${stepsHtml || `<div class="empty">No workflow steps yet.</div>`}
        </div>
    </div>
</body>
</html>`;
};

// Renders the document in an off-screen iframe and opens the browser's print
// dialog on it. An iframe (rather than window.open) keeps popup blockers out of
// the way, and the frame is torn down once printing finishes.
export const printRequestDocument = (options) => {
    const html = buildRequestPrintHtml(options);

    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(frame);

    let removed = false;
    const cleanup = () => {
        if (removed) return;
        removed = true;
        if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    frame.onload = () => {
        const frameWindow = frame.contentWindow;
        if (!frameWindow) {
            cleanup();
            return;
        }
        // Safari never fires afterprint on the frame, so the timer is the backstop
        // that removes it either way.
        frameWindow.onafterprint = cleanup;
        frameWindow.focus();
        frameWindow.print();
        setTimeout(cleanup, 60000);
    };

    const doc = frame.contentWindow?.document;
    if (!doc) {
        cleanup();
        return;
    }
    doc.open();
    doc.write(html);
    doc.close();
};
